import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';

import { 
  Car, 
  MessageSquare, 
  Users, 
  TrendingUp,
  ArrowUpRight,
  Clock,
  Loader2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  CheckCircle2,
  X
} from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';

const Dashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Stats
        const statsRes = await fetch(`${API_URL}/stats`);
        const stats = await statsRes.json();
        setStatsData(stats);

        // Fetch All Vehicles for Calendar (Admin endpoint for full access)
        const vehiclesRes = await fetch(`${API_URL}/admin/vehicles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const vehiclesData = await vehiclesRes.json();
        setVehicles(vehiclesData); // Use all vehicles for the reservation selector

        // Fetch Recent Quotes (Replaced Messages with Quotes)
        const quotesRes = await fetch(`${API_URL}/admin/quotes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const quotesData = await quotesRes.json();
        setQuotes(quotesData.slice(0, 4));

        // Fetch Availability
        const availabilityRes = await fetch(`${API_URL}/admin/availability`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const availabilityData = await availabilityRes.json();
        setAvailability(Array.isArray(availabilityData) ? availabilityData : []);

      } catch (err) {
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token]);

  const [availability, setAvailability] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newReservation, setNewReservation] = useState({
    vehicle_id: '',
    start_date: '',
    end_date: '',
    type: 'Kiralama',
    notes: ''
  });

  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [activeDayLabel, setActiveDayLabel] = useState('');
  const [selectedDateStr, setSelectedDateStr] = useState('');

  const typeOptions = ["Kiralama", "Rezerve", "Bakım", "Serviste", "Temizlikte", "Kapora Alındı"];

  const stats = [
    { title: 'Toplam Araç', value: statsData?.totalVehicles || '0', icon: <Car />, trend: 'Yayında' },
    { title: 'Bekleyen Teklifler', value: statsData?.newQuotes || '0', icon: <Clock />, trend: 'Yeni' },
    { title: 'Bekleyen Mesajlar', value: statsData?.newMessages || '0', icon: <MessageSquare />, trend: 'Yeni' },
  ];

  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleAddReservation = async (e) => {
    e.preventDefault();
    
    // Show loading alert
    Swal.fire({
      title: editingId ? 'Güncelleniyor...' : 'Kaydediliyor...',
      html: 'Planlama takvime işleniyor, lütfen bekleyin.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
      background: '#0f172a',
      color: '#fff'
    });

    try {
      const url = editingId 
        ? `${API_URL}/admin/availability/${editingId}`
        : `${API_URL}/admin/availability`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newReservation)
      });
      
      if (response.ok) {
        setIsModalOpen(false);
        setEditingId(null);
        // Refresh availability
        const availabilityRes = await fetch(`${API_URL}/admin/availability`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const availabilityData = await availabilityRes.json();
        setAvailability(Array.isArray(availabilityData) ? availabilityData : []);
        setNewReservation({ vehicle_id: '', start_date: '', end_date: '', type: 'Kiralama', notes: '' });

        // Success Alert
        Swal.fire({
          icon: 'success',
          title: 'Başarılı!',
          text: editingId ? 'Planlama güncellendi.' : 'Takvim planlaması başarıyla kaydedildi.',
          timer: 2000,
          showConfirmButton: false,
          background: '#0f172a',
          color: '#fff'
        });
      } else {
        throw new Error('Kayıt başarısız');
      }
    } catch (err) {
      console.error('Rezervasyon hatası:', err);
      Swal.fire({
        icon: 'error',
        title: 'Hata!',
        text: 'İşlem sırasında bir sorun oluştu. Lütfen bilgileri kontrol edin.',
        background: '#0f172a',
        color: '#fff'
      });
    }
  };

  const handleEditClick = (item, e) => {
    e.stopPropagation();
    setEditingId(item.id);
    setNewReservation({
      vehicle_id: item.vehicle_id,
      start_date: item.start_date.slice(0, 16),
      end_date: item.end_date.slice(0, 16),
      type: item.type,
      notes: item.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      const response = await fetch(`${API_URL}/admin/availability/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAvailability(availability.filter(a => a.id !== id));
      }
    } catch (err) {
      console.error('Silme hatası:', err);
    }
  };

  const handleDayMouseDown = (dateStr) => {
    setSelectionStart(dateStr);
    setSelectionEnd(dateStr);
    setIsSelecting(true);
  };

  const handleDayMouseEnter = (dateStr) => {
    if (isSelecting) {
      setSelectionEnd(dateStr);
    }
  };

  const handleDayMouseUp = () => {
    if (isSelecting && selectionStart && selectionEnd) {
      // Sürükleme yapılmışsa (farklı günler seçilmişse) direkt ekleme modalını aç
      if (selectionStart !== selectionEnd) {
        const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
        const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
        
        setNewReservation({
          ...newReservation,
          start_date: `${start}T09:00`,
          end_date: `${end}T18:00`
        });
        setEditingId(null);
        setIsModalOpen(true);
      }
    }
    setIsSelecting(false);
  };

  const handleDayClick = (dateStr, events, e) => {
    // Çoklu seçim yapılmışsa bu fonksiyon işlem yapmasın (mouseUp halletti)
    if (selectionStart !== selectionEnd) return;
    
    if (events.length > 0) {
      // Dolu gün: Önce özet penceresi açılsın
      setSelectedDayEvents(events);
      setActiveDayLabel(new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }));
      setSelectedDateStr(dateStr);
      setIsDayModalOpen(true);
    } else {
      // Boş gün: Direkt ekleme penceresi açılsın
      setEditingId(null);
      setNewReservation({
        vehicle_id: '',
        start_date: `${dateStr}T09:00`,
        end_date: `${dateStr}T18:00`,
        type: 'Kiralama',
        notes: ''
      });
      setIsModalOpen(true);
    }
  };

  const isInSelection = (dateStr) => {
    if (!selectionStart || !selectionEnd) return false;
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
    return dateStr >= start && dateStr <= end;
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(year, month);
    const startDay = (firstDayOfMonth(year, month) + 6) % 7; // Monday start
    const today = new Date();
    
    const calendarDays = [];
    // Previous month days
    for (let i = 0; i < startDay; i++) {
        calendarDays.push(<div key={`prev-${i}`} style={{ height: '100px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)' }}></div>);
    }
    
    // Current month days
    for (let day = 1; day <= days; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayAvailability = Array.isArray(availability) ? availability.filter(a => {
            const start = a.start_date?.split('T')[0];
            const end = a.end_date?.split('T')[0];
            return dateStr >= start && dateStr <= end;
        }) : [];

        const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
        const isSelected = isInSelection(dateStr);

        calendarDays.push(
            <div key={day} 
                onMouseDown={() => handleDayMouseDown(dateStr)}
                onMouseEnter={() => handleDayMouseEnter(dateStr)}
                onMouseUp={handleDayMouseUp}
                onClick={(e) => handleDayClick(dateStr, dayAvailability, e)}
                style={{ 
                    height: '110px', 
                    background: isSelected ? 'rgba(0, 209, 255, 0.15)' : 'rgba(255,255,255,0.02)', 
                    border: isSelected ? '1px solid rgba(0, 209, 255, 0.4)' : '1px solid rgba(255,255,255,0.05)',
                    padding: '0.4rem',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    position: 'relative',
                    transition: 'all 0.1s',
                    cursor: 'cell',
                    userSelect: 'none',
                    zIndex: isSelected ? 2 : 1,
                    overflow: 'hidden'
                }}>
                <span style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: isToday || isSelected ? '900' : '600', 
                    color: isToday ? 'var(--primary)' : isSelected ? '#fff' : 'var(--text-dim)',
                    background: isToday ? 'rgba(0, 209, 255, 0.1)' : 'transparent',
                    borderRadius: '4px',
                    padding: '1px 3px',
                    width: 'fit-content',
                    marginBottom: '2px'
                }}>{day}</span>
                
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayAvailability.slice(0, 2).map(a => (
                        <div key={a.id} style={{ 
                            fontSize: '0.62rem', 
                            padding: '2px 4px', 
                            borderRadius: '3px',
                            background: a.type === 'Bakım' ? 'rgba(239, 68, 68, 0.2)' : 
                                       a.type === 'Rezerve' ? 'rgba(245, 158, 11, 0.2)' : 
                                       'rgba(34, 197, 94, 0.2)',
                            color: a.type === 'Bakım' ? '#f87171' : 
                                   a.type === 'Rezerve' ? '#fbbf24' : 
                                   '#4ade80',
                            borderLeft: `2px solid ${
                                a.type === 'Bakım' ? '#ef4444' : 
                                a.type === 'Rezerve' ? '#f59e0b' : 
                                '#22c55e'}`,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {a.vehicle_name}
                        </div>
                    ))}
                    {dayAvailability.length > 2 && (
                        <div style={{ fontSize: '0.6rem', color: 'var(--primary)', fontWeight: '800', textAlign: 'center', marginTop: '2px', background: 'rgba(0, 209, 255, 0.05)', borderRadius: '4px' }}>
                            + {dayAvailability.length - 2} kayıt daha
                        </div>
                    )}
                </div>
            </div>
        );
    }
    return calendarDays;
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '1rem', color: 'var(--text-dim)' }}>
      <Loader2 className="animate-spin" size={48} color="var(--primary)" />
      <p>Dashboard Verileri Yükleniyor...</p>
    </div>
  );

  return (
    <div className="dashboard-content" style={{ paddingBottom: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.5rem' }}>Hoş Geldiniz, Admin</h1>
        <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(0.85rem, 2vw, 1rem)' }}>Erenis Otomotiv'in bugünkü performans özetini aşağıda görebilirsiniz.</p>
      </div>

      <div className="stat-grid">
        {stats.map((stat, idx) => (
          <div key={idx} className="stat-card">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-info">
              <h3>{stat.title}</h3>
              <div className="value">{stat.value}</div>
              <div style={{ fontSize: '0.75rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                <ArrowUpRight size={12} /> {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* ARAÇ MÜSAİTLİK TAKVİMİ (GOOGLE CALENDAR STYLE) */}
      <div className="admin-card" style={{ marginBottom: '2.5rem', overflow: 'visible' }}>
          <div className="admin-card-header" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(0, 209, 255, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                      <CalendarIcon size={22} />
                  </div>
                  <div>
                      <h2 className="admin-card-title" style={{ marginBottom: '0.1rem' }}>Araç Müsaitlik & Rezervasyon Takvimi</h2>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Filo planlamasını buradan yapabilirsiniz.</p>
                  </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.03)', padding: '0.5rem 1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <button onClick={handlePrevMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex' }}><ChevronLeft size={20} /></button>
                      <span style={{ fontWeight: '800', fontSize: '0.95rem', minWidth: '130px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
                          {currentDate.toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                      </span>
                      <button onClick={handleNextMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex' }}><ChevronRight size={20} /></button>
                  </div>
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    style={{ background: 'var(--primary)', color: 'black', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '10px', fontWeight: '800', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', boxShadow: '0 8px 20px rgba(0, 209, 255, 0.2)' }}
                  >
                      <Plus size={18} /> Yeni Kayıt
                  </button>
              </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0px', borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
              {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(d => (
                  <div key={d} style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.03)', textAlign: 'center', fontSize: '0.75rem', fontWeight: '900', color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.08)', textTransform: 'uppercase' }}>{d}</div>
              ))}
              {renderCalendar()}
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.25rem', padding: '0 0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#22c55e' }}></div> KİRALAMA
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#f59e0b' }}></div> REZERVE
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: '#ef4444' }}></div> BAKIM
              </div>
          </div>
      </div>

       {/* Reservation Modal */}
       {isModalOpen && (
           <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
               <div style={{ background: '#0f172a', width: '100%', maxWidth: '520px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', padding: '2.5rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', position: 'relative' }}>
                   <button 
                       type="button" 
                       onClick={() => setIsModalOpen(false)} 
                       style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' }}
                       onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                       onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                   >
                       <X size={20} />
                   </button>

                   <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: '800', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                       <CalendarIcon size={22} color="var(--primary)" /> {editingId ? 'Planlamayı Düzenle' : 'Yeni Planlama Ekle'}
                   </h3>
                   <form onSubmit={handleAddReservation} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                       <div>
                           <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase' }}>ARAÇ SEÇİN</label>
                           <select 
                             required
                             value={newReservation.vehicle_id}
                             onChange={(e) => setNewReservation({...newReservation, vehicle_id: e.target.value})}
                             style={{ width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem', color: 'white', outline: 'none' }}
                           >
                               <option value="" style={{ background: '#0f172a' }}>Araç Seçiniz...</option>
                               {vehicles.map(v => (
                                 <option key={v.id} value={v.id} style={{ background: '#0f172a' }}>
                                   {v.brand} {v.model} — {v.name} {v.license_plate ? `[${v.license_plate}]` : ''}
                                 </option>
                               ))}
                           </select>
                       </div>
                       <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                           <div>
                               <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase' }}>BAŞLANGIÇ</label>
                               <div style={{ position: 'relative' }}>
                                   <input 
                                     type="datetime-local" required 
                                     value={newReservation.start_date}
                                     onChange={(e) => setNewReservation({...newReservation, start_date: e.target.value})}
                                     style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem 2.8rem 0.8rem 0.5rem', color: 'white', outline: 'none', colorScheme: 'dark', fontSize: '0.85rem' }} 
                                   />
                                   <button 
                                     type="button"
                                     onClick={(e) => { e.currentTarget.previousSibling.blur(); }}
                                     style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', width: '24px', height: '24px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                                   >
                                     <X size={14} strokeWidth={3} />
                                   </button>
                               </div>
                               <small style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '0.3rem', display: 'block' }}>Kapatmak için X'e basın.</small>
                           </div>
                           <div>
                               <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase' }}>BİTİŞ</label>
                               <div style={{ position: 'relative' }}>
                                   <input 
                                     type="datetime-local" required 
                                     value={newReservation.end_date}
                                     onChange={(e) => setNewReservation({...newReservation, end_date: e.target.value})}
                                     style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem 2.8rem 0.8rem 0.5rem', color: 'white', outline: 'none', colorScheme: 'dark', fontSize: '0.85rem' }} 
                                   />
                                   <button 
                                     type="button"
                                     onClick={(e) => { e.currentTarget.previousSibling.blur(); }}
                                     style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', width: '24px', height: '24px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                                   >
                                     <X size={14} strokeWidth={3} />
                                   </button>
                               </div>
                               <small style={{ fontSize: '0.6rem', color: '#64748b', marginTop: '0.3rem', display: 'block' }}>Kapatmak için X'e basın.</small>
                           </div>
                       </div>
                      <div style={{ position: 'relative' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase' }}>TÜR</label>
                          <div style={{ position: 'relative' }}>
                              <input 
                                value={newReservation.type}
                                onChange={(e) => setNewReservation({...newReservation, type: e.target.value})}
                                onFocus={() => setIsTypeDropdownOpen(true)}
                                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                                placeholder="Seçiniz veya yazınız..."
                                style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem 2.5rem 0.8rem 0.8rem', color: 'white', outline: 'none' }}
                              />
                              <div 
                                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-dim)', transform: isTypeDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)', transition: 'all 0.2s' }}
                              >
                                  <ChevronRight size={18} style={{ transform: 'rotate(90deg)' }} />
                              </div>
                          </div>
                          
                          {isTypeDropdownOpen && (
                              <>
                                <div 
                                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }} 
                                    onClick={() => setIsTypeDropdownOpen(false)}
                                />
                                <div style={{ 
                                    position: 'absolute', bottom: '110%', left: 0, right: 0, 
                                    background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', 
                                    borderRadius: '12px', overflow: 'hidden', zIndex: 99999,
                                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                                }}>
                                    {typeOptions.map(opt => (
                                        <div 
                                            key={opt}
                                            onClick={() => {
                                                setNewReservation({...newReservation, type: opt});
                                                setIsTypeDropdownOpen(false);
                                            }}
                                            style={{ padding: '0.8rem 1rem', color: 'white', fontSize: '0.85rem', cursor: 'pointer', background: newReservation.type === opt ? 'rgba(0, 209, 255, 0.1)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                            onMouseOut={(e) => e.currentTarget.style.background = newReservation.type === opt ? 'rgba(0, 209, 255, 0.1)' : 'transparent'}
                                        >
                                            {opt}
                                        </div>
                                    ))}
                                </div>
                              </>
                          )}
                      </div>
                      <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block', textTransform: 'uppercase' }}>NOTLAR</label>
                          <textarea 
                            value={newReservation.notes}
                            onChange={(e) => setNewReservation({...newReservation, notes: e.target.value})}
                            style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem', color: 'white', outline: 'none', minHeight: '80px', resize: 'vertical' }} placeholder="Müşteri adı, bakım detayı vb." />
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                          <button type="button" onClick={() => { setIsModalOpen(false); setEditingId(null); setNewReservation({ vehicle_id: '', start_date: '', end_date: '', type: 'Kiralama', notes: '' }); }} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', color: 'white', border: 'none', padding: '1rem', borderRadius: '15px', fontWeight: '700', cursor: 'pointer' }}>İptal</button>
                          
                          {editingId && (
                            <button type="button" onClick={() => { handleDeleteReservation(editingId); setIsModalOpen(false); setEditingId(null); }} style={{ flex: 1, background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '1rem', borderRadius: '15px', fontWeight: '700', cursor: 'pointer' }}>Sil</button>
                          )}

                          <button type="submit" style={{ flex: 2, background: 'var(--primary)', color: 'black', border: 'none', padding: '1rem', borderRadius: '15px', fontWeight: '900', cursor: 'pointer' }}>{editingId ? 'Güncelle' : 'Kaydet'}</button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* Day Details Modal */}
      {isDayModalOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
              <div style={{ background: '#0f172a', width: '100%', maxWidth: '600px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.1)', padding: '2rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                        <h3 style={{ color: 'white', fontSize: '1.4rem', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <CalendarIcon size={24} color="var(--primary)" /> {activeDayLabel} — Gün Özeti
                        </h3>
                        <button onClick={() => setIsDayModalOpen(false)} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white', cursor: 'pointer', padding: '0.5rem', borderRadius: '10px' }}><X size={20} /></button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
                      {selectedDayEvents.map(item => (
                          <div key={item.id} style={{ 
                              background: 'rgba(255,255,255,0.03)', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                          }}>
                              <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.3rem' }}>
                                      <div style={{ fontSize: '0.7rem', fontWeight: '900', padding: '2px 8px', borderRadius: '6px', background: item.type === 'Bakım' ? '#ef4444' : item.type === 'Rezerve' ? '#f59e0b' : '#22c55e', color: 'black', textTransform: 'uppercase' }}>{item.type}</div>
                                      <span style={{ fontWeight: '800', color: 'white', fontSize: '1.05rem' }}>{item.vehicle_name}</span>
                                  </div>
                                  <div style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500' }}>
                                      {new Date(item.start_date).toLocaleTimeString('tr-TR', {hour:'2-digit', minute:'2-digit'})} ile {new Date(item.end_date).toLocaleTimeString('tr-TR', {hour:'2-digit', minute:'2-digit'})} arası
                                  </div>
                                  {item.notes && <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem', fontStyle: 'italic' }}>"{item.notes}"</div>}
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button 
                                    onClick={(e) => { handleEditClick(item, e); setIsDayModalOpen(false); }}
                                    style={{ background: 'var(--primary)', color: 'black', border: 'none', padding: '0.5rem 1rem', borderRadius: '10px', fontWeight: '800', fontSize: '0.75rem', cursor: 'pointer' }}
                                  >Düzenle</button>
                                  <button 
                                    onClick={() => { handleDeleteReservation(item.id); setIsDayModalOpen(false); }}
                                    style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '0.5rem', borderRadius: '10px', cursor: 'pointer' }}
                                  ><Trash2 size={16} /></button>
                              </div>
                          </div>
                      ))}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                        <button 
                            onClick={() => setIsDayModalOpen(false)}
                            style={{ flex: 1, padding: '1rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: 'none', borderRadius: '15px', fontWeight: '700', cursor: 'pointer' }}
                        >Kapat</button>
                        <button 
                            onClick={() => {
                                setIsDayModalOpen(false);
                                setEditingId(null);
                                const date = new Date(selectedDateStr);
                                const startStr = `${selectedDateStr}T09:00`;
                                const endStr = `${selectedDateStr}T18:00`;
                                setNewReservation({ vehicle_id: '', start_date: startStr, end_date: endStr, type: 'Kiralama', notes: '' });
                                setIsModalOpen(true);
                            }}
                            style={{ flex: 2, padding: '1rem', background: 'var(--primary)', color: 'black', border: 'none', borderRadius: '15px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                        >
                            <Plus size={20} strokeWidth={3} /> YENİ PLANLAMA EKLE
                        </button>
                  </div>
              </div>
          </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Recent Vehicles Table */}
        <div className="admin-card lg:col-span-8">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Son Eklenen Araçlar</h2>
            <button style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>Hepsini Gör</button>
          </div>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Araç Adı</th>
                  <th>Tür</th>
                  <th>Fiyat</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.slice(0, 4).map((car) => (
                  <tr key={car.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '40px', height: '30px', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--navbar-border)', background: 'var(--bg-secondary)', flexShrink: 0 }}>
                          <img src={car.images[0]} alt={car.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <span style={{ fontWeight: '600', whiteSpace: 'nowrap' }}>{car.name}</span>
                      </div>
                    </td>
                    <td>{car.type}</td>
                    <td style={{ color: 'var(--primary)', fontWeight: '700' }}>{car.price}</td>
                    <td>
                      <span className={`badge ${car.status === 'Aktif' || car.status === 'Yayında' ? 'badge-success' : 'badge-warning'}`}>
                        {car.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>Henüz araç eklenmemiş.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity / Quotes */}
        <div className="admin-card lg:col-span-4">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Son Teklifler</h2>
            <button 
              onClick={() => navigate('/admin/quotes')}
              style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              Hepsini Gör
            </button>
          </div>
          <div style={{ padding: '1rem' }}>
            {quotes.map((quote, i) => (
              <div key={quote.id} style={{ padding: '1rem', borderBottom: i === quotes.length - 1 ? 'none' : '1px solid var(--navbar-border)', display: 'flex', gap: '1rem' }}>
                <div style={{ width: '45px', height: '45px', borderRadius: '10px', background: 'var(--bg-secondary)', overflow: 'hidden', border: '1px solid var(--navbar-border)' }}>
                  {quote.vehicle_image ? (
                    <img src={quote.vehicle_image} alt={quote.vehicle_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Clock size={18} color="var(--text-dim)" />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.25rem' }}>{quote.name} {quote.surname}</div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', lineHeight: '1.4' }}>
                    <span style={{ color: 'var(--primary)', fontWeight: '700' }}>{quote.vehicle_name}</span> - {quote.service_type === 'rental' ? 'Kiralama' : 'Satın Alma'}
                  </p>
                  <span style={{ fontSize: '0.7rem', color: 'var(--primary)', marginTop: '0.5rem', display: 'block' }}>
                    {new Date(quote.created_at).toLocaleString('tr-TR')}
                  </span>
                </div>
              </div>
            ))}
            {quotes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-dim)' }}>Henüz teklif talebi yok.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
